import {
	Check,
	ContactPageOutlined,
	ContentCopyOutlined,
	Email,
	Euro,
	FolderOpen,
	Language,
	LinkedIn,
	LocationOn,
	Phone,
	RecordVoiceOverOutlined,
	ModeEdit
} from "@mui/icons-material";
import {
	Avatar,
	Box,
	Card,
	CardContent,
	CardHeader,
	Chip,
	Divider,
	Fade,
	Grid2,
	IconButton,
	Link,
	Paper,
	Stack,
	Tab,
	Tabs,
	Typography
} from "@mui/material";
import { Link as NavigationLink } from "react-router-dom";
import { useCallback, useEffect, useRef, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import HtmlEditor from "../UI/HtmlEditor";
import { useFreelancerProfile } from "../../hooks/useFreelancerProfile";
import { useLatestNoteOfSpecificApplication } from "../../hooks/useApplicationNote";
import { useCurrentUser } from "../../hooks/useUser";
import { DocumentType } from "../../models/enums/DocumentType";
import { LanguageProficiency } from "../../models/enums/LanguageProficiency";
import { SkillLevel } from "../../models/enums/SkillLevel";
import { ToolLevel } from "../../models/enums/ToolLevel";
import { UrlType } from "../../models/enums/UrlType";
import { FreelancerProfile } from "../../models/FreelancerProfile";
import { FreelancerProfileDocument } from "../../models/FreelancerProfileDocument";
import { FreelancerProfileProfessionalExperience } from "../../models/FreelancerProfileProfessionalExperience";
import { User } from "../../models/User";
import { ApplicationNote } from "../../models/ApplicationNote";
import { zoullooColors } from "../../theme";
import { calculateDuration, formatDateForDisplay } from "../../utils/dateUtils";
import { stringAvatar } from "../../utils/avatarUtils";
import DocumentTable from "./DocumentTable";
import UrlTable from "../freelancer/onboarding/components/common/UrlTable";
import ErrorDisplay from "../internal-user/applications/ErrorDisplay";
import { ApplicationNotes } from "../internal-user/applications/talent";
import FreelancerDetailsSkeleton from "./components/freelancerDetails/FreelancerDetailsSkeleton";
import LastApplicationNoteTakenSkeleton from "./components/freelancerDetails/LastApplicationNoteTakenSkeleton";
import LastApplicationNoteTakenErrorDisplay from "./components/freelancerDetails/LastApplicationNoteTakenErrorDisplay";
import LastApplicationNoteTakenNotFound from "./components/freelancerDetails/LastApplicationNoteTakenNotFound";
import FreelancerNotFound from "./components/freelancerDetails/FreelancerNotFound";
import { UserRole } from "../../models/UserRole";
import { FreelancerOnboardingStatus } from "../../models/enums/FreelancerOnboardingStatus";
import { UserType } from "../../models/UserType";
import i18n from "../../i18n/i18n";
import { useFreelancerProfileEvaluations } from "../../hooks/useProjectEvaluations";
import { useEnvironmentNavigation } from "../../hooks/useEnvironmentNavigation";
import { useEntityTranslations } from "../../hooks/useTranslations";
import { LanguageCode } from "../../models/enums/LanguageCode";
import {
	resolveTranslatableFieldValue,
	translatableFieldEmptyLabel
} from "../../utils/translatableFieldValue";
import { useLanguage } from "../../i18n/LanguageContext";
import TranslatableFieldWrapper from "../translation/TranslatableFieldWrapper";
import StarRating from "../project/evaluation/StarRating";
import EvaluationDisplay from "../project/evaluation/EvaluationDisplay";
import { FreelancerProfileEvaluationSummary, EvaluatorType } from "../../models/ProjectEvaluation";

interface FreelancerDetailsProps {
	readonly freelancerProfileId: number;
	readonly isExpanded: boolean;
	readonly freelancerProfileKey?: string;
	readonly hideLastExchange?: boolean;
	readonly showSocialNetworks?: boolean;
	readonly showDocuments?: boolean;
	readonly showAvailability?: boolean;
	readonly showAdditionalInfo?: boolean;
	readonly showContact?: boolean;
	readonly showNotes?: boolean;
	readonly showHeader?: boolean;
	readonly showEvaluations?: boolean;
	readonly presentationActions?: React.ReactNode;
}

// Helper component for rendering chip lists
const ChipListSection = ({
	items,
	label,
	t
}: {
	items: string[];
	label: string;
	t: TFunction;
}) => (
	<Card
		component={Paper}
		elevation={0}
		sx={{
			margin: "16px",
			borderRadius: "8px"
		}}
	>
		<CardHeader title={<Typography variant="h6">{t(label)}</Typography>} />
		<Divider />
		<CardContent sx={{ p: 1.5 }}>
			{items.length === 0 ? (
				<Typography
					variant="body1"
					color="text.secondary"
					style={{ fontStyle: "italic", textAlign: "center" }}
				>
					{t("freelancerDetails.notProvided")}
				</Typography>
			) : (
				<Box display="flex" flexWrap="wrap" gap={0.5}>
					{items.map(item => (
						<Chip
							key={item}
							label={<Typography variant="body2">{item}</Typography>}
							size="medium"
							variant="outlined"
						/>
					))}
				</Box>
			)}
		</CardContent>
	</Card>
);

// Helper component for rendering Sectors and Domains section
const SectorsAndDomainsSection = ({
	sectors,
	domains,
	t
}: {
	sectors: string[];
	domains: string[];
	t: TFunction;
}) => (
	<Card
		component={Paper}
		elevation={0}
		sx={{
			margin: "16px",
			borderRadius: "8px"
		}}
	>
		<CardHeader
			title={
				<Typography variant="h6">
					{t("freelancerDetails.sections.sectorsAndDomains")}
				</Typography>
			}
		/>
		<Divider />
		<CardContent sx={{ p: 1.5 }}>
			{sectors.length === 0 && domains.length === 0 ? (
				<Typography
					variant="body1"
					color="text.secondary"
					style={{ fontStyle: "italic", textAlign: "center" }}
				>
					{t("freelancerDetails.notProvided")}
				</Typography>
			) : (
				<Grid2 container spacing={2}>
					<Grid2 size={6}>
						<Typography
							variant="body1"
							color="text.secondary"
							mb={2}
							display="block"
							fontSize={"0.95rem"}
						>
							{t("freelancerDetails.sections.sectors")}
						</Typography>
						{sectors.length === 0 ? (
							<Typography
								variant="body1"
								color="text.secondary"
								style={{ fontStyle: "italic", textAlign: "center" }}
							>
								{t("freelancerDetails.notProvided")}
							</Typography>
						) : (
							<Box display="flex" flexWrap="wrap" gap={0.5}>
								{sectors.map(item => (
									<Chip
										key={item}
										label={<Typography variant="body2">{item}</Typography>}
										size="medium"
										variant="outlined"
									/>
								))}
							</Box>
						)}
					</Grid2>
					<Grid2 size={6}>
						<Typography
							variant="body1"
							color="text.secondary"
							mb={2}
							display="block"
							fontSize={"0.95rem"}
						>
							{t("freelancerDetails.sections.domains")}
						</Typography>
						{domains.length === 0 ? (
							<Typography
								variant="body1"
								color="text.secondary"
								style={{ fontStyle: "italic", textAlign: "center" }}
							>
								{t("freelancerDetails.notProvided")}
							</Typography>
						) : (
							<Box display="flex" flexWrap="wrap" gap={0.5}>
								{domains.map(item => (
									<Chip
										key={item}
										label={<Typography variant="body2">{item}</Typography>}
										size="medium"
										variant="outlined"
									/>
								))}
							</Box>
						)}
					</Grid2>
				</Grid2>
			)}
		</CardContent>
	</Card>
);

// Helper component for rendering skills section
const SkillsSection = ({
	title,
	expertises,
	confirmed,
	t
}: {
	title: string;
	expertises: string[];
	confirmed: string[];
	t: TFunction;
}) => (
	<Card
		component={Paper}
		elevation={0}
		sx={{
			margin: "16px",
			borderRadius: "8px"
		}}
	>
		<CardHeader title={<Typography variant="h6">{t(title)}</Typography>} />
		<Divider />
		<CardContent sx={{ p: 1.5 }}>
			{expertises.length === 0 && confirmed.length === 0 ? (
				<Typography
					variant="body1"
					color="text.secondary"
					style={{ fontStyle: "italic", textAlign: "center" }}
				>
					{t("freelancerDetails.notProvided")}
				</Typography>
			) : (
				<Grid2 container spacing={2}>
					<Grid2 size={6}>
						<Typography
							variant="body1"
							color="text.secondary"
							mb={2}
							display="block"
							fontSize={"0.95rem"}
						>
							{t(`enums.SkillLevel.${SkillLevel.VeryGood}`)}
						</Typography>
						{expertises.length === 0 ? (
							<Typography
								variant="body1"
								color="text.secondary"
								style={{ fontStyle: "italic", textAlign: "center" }}
							>
								{t("freelancerDetails.notProvided")}
							</Typography>
						) : (
							<Box display="flex" flexWrap="wrap" gap={0.5}>
								{expertises.map(item => (
									<Chip
										key={item}
										label={<Typography variant="body2">{item}</Typography>}
										size="medium"
										variant="outlined"
									/>
								))}
							</Box>
						)}
					</Grid2>
					<Grid2 size={6}>
						<Typography
							variant="body1"
							color="text.secondary"
							mb={2}
							display="block"
							fontSize={"0.95rem"}
						>
							{t(`enums.SkillLevel.${SkillLevel.Good}`)}
						</Typography>
						{confirmed.length === 0 ? (
							<Typography
								variant="body1"
								color="text.secondary"
								style={{ fontStyle: "italic", textAlign: "center" }}
							>
								{t("freelancerDetails.notProvided")}
							</Typography>
						) : (
							<Box display="flex" flexWrap="wrap" gap={0.5}>
								{confirmed.map(item => (
									<Chip
										key={item}
										label={<Typography variant="body2">{item}</Typography>}
										size="medium"
										variant="outlined"
									/>
								))}
							</Box>
						)}
					</Grid2>
				</Grid2>
			)}
		</CardContent>
	</Card>
);

// Helper component for rendering tools section
const ToolsSection = ({
	title,
	mastered,
	used,
	t
}: {
	title: string;
	mastered: string[];
	used: string[];
	t: TFunction;
}) => (
	<Card
		component={Paper}
		elevation={0}
		sx={{
			margin: "16px",
			borderRadius: "8px"
		}}
	>
		<CardHeader title={<Typography variant="h6">{t(title)}</Typography>} />
		<Divider />
		<CardContent sx={{ p: 1.5 }}>
			{mastered.length === 0 && used.length === 0 ? (
				<Typography
					variant="body1"
					color="text.secondary"
					style={{ fontStyle: "italic", textAlign: "center" }}
				>
					{t("freelancerDetails.notProvided")}
				</Typography>
			) : (
				<Grid2 container spacing={2}>
					<Grid2 size={6}>
						<Typography
							variant="body1"
							color="text.secondary"
							mb={2}
							display="block"
							fontSize={"0.95rem"}
						>
							{t(`enums.ToolLevel.${ToolLevel.Mastered}`)}
						</Typography>
						{mastered.length === 0 ? (
							<Typography
								variant="body1"
								color="text.secondary"
								style={{ fontStyle: "italic", textAlign: "center" }}
							>
								{t("freelancerDetails.notProvided")}
							</Typography>
						) : (
							<Box display="flex" flexWrap="wrap" gap={0.5}>
								{mastered.map(item => (
									<Chip
										key={item}
										label={<Typography variant="body2">{item}</Typography>}
										size="medium"
										variant="outlined"
									/>
								))}
							</Box>
						)}
					</Grid2>
					<Grid2 size={6}>
						<Typography
							variant="body1"
							color="text.secondary"
							mb={2}
							display="block"
							fontSize={"0.95rem"}
						>
							{t(`enums.ToolLevel.${ToolLevel.AlreadyUsed}`)}
						</Typography>
						{used.length === 0 ? (
							<Typography
								variant="body1"
								color="text.secondary"
								style={{ fontStyle: "italic", textAlign: "center" }}
							>
								{t("freelancerDetails.notProvided")}
							</Typography>
						) : (
							<Box display="flex" flexWrap="wrap" gap={0.5}>
								{used.map(item => (
									<Chip
										key={item}
										label={<Typography variant="body2">{item}</Typography>}
										size="medium"
										variant="outlined"
									/>
								))}
							</Box>
						)}
					</Grid2>
				</Grid2>
			)}
		</CardContent>
	</Card>
);

// Custom hook for preparing freelancer data
const useFreelancerData = (
	freelancerProfile: FreelancerProfile | null | undefined,
	t: TFunction
) => {
	return useMemo(
		() => ({
			sectors: freelancerProfile?.sectors.map(x => t(`Sectors.${x.sector.label}`)) || [],
			businessDomains:
				freelancerProfile?.businessDomains.map(x =>
					t(`BusinessDomains.${x.businessDomain.label}`)
				) || [],
			skills: {
				expertises:
					freelancerProfile?.skills
						.filter(x => x.level === SkillLevel.VeryGood)
						.map(x => t(`Skills.${x.skill.label}`)) || [],
				confirmed:
					freelancerProfile?.skills
						.filter(x => x.level === SkillLevel.Good)
						.map(x => t(`Skills.${x.skill.label}`)) || []
			},
			tools: {
				mastered:
					freelancerProfile?.tools
						.filter(x => x.level === ToolLevel.Mastered)
						.map(x => x.tool.label || "")
						.filter((x: string) => x !== "") || [],
				used:
					freelancerProfile?.tools
						.filter(x => x.level === ToolLevel.AlreadyUsed)
						.map(x => x.tool.label || "")
						.filter((x: string) => x !== "") || []
			},
			languages: {
				maternal:
					freelancerProfile?.freelancer?.languages
						.filter(x => x.languageProficiency === LanguageProficiency.Native)
						.map(x => t(`Languages.${x.language.label}`)) || [],
				fluent:
					freelancerProfile?.freelancer?.languages
						.filter(x => x.languageProficiency === LanguageProficiency.Fluent)
						.map(x => t(`Languages.${x.language.label}`)) || [],
				operational:
					freelancerProfile?.freelancer?.languages
						.filter(x => x.languageProficiency === LanguageProficiency.Operational)
						.map(x => t(`Languages.${x.language.label}`)) || []
			},
			regions: freelancerProfile?.regions.map(x => t(`Regions.${x.region.label}`)) || [],
			preferences: [
				`${t("freelancerDetails.preferences.location")} : ${
					freelancerProfile?.jobPreferredLocation?.label
						? t(`JobLocations.${freelancerProfile.jobPreferredLocation.label}`)
						: t("freelancerDetails.notProvided")
				}`,
				`${t("freelancerDetails.preferences.missionDuration")} : ${
					freelancerProfile?.missionsPreferredDuration?.label
						? t(`MissionsDurations.${freelancerProfile.missionsPreferredDuration.label}`)
						: t("freelancerDetails.notProvided")
				}`
			],
			professionalExperiences: freelancerProfile?.professionalExperiences || []
		}),
		[freelancerProfile, t]
	);
};

// Helper component for sections with bordered container and header
const SectionContainer = ({
	title,
	children
}: {
	title: string;
	children: React.ReactNode;
}) => (
	<Card
		sx={{
			margin: "16px"
		}}
	>
		<CardHeader title={<Typography variant="h6">{title}</Typography>} />
		<Divider />
		<CardContent>{children}</CardContent>
	</Card>
);

// Helper component for Online Presence section
const OnlinePresenceSection = ({
	freelancerProfile,
	t
}: {
	freelancerProfile: FreelancerProfile;
	t: TFunction;
}) => (
	<SectionContainer title={t("freelancerDetails.onlinePresence")}>
		<UrlTable
			urls={freelancerProfile.urls.map(url => ({
				id: url.id,
				url: url.url,
				name: url.url,
				type: url.type
			}))}
			displayMode={true}
			showHeader={false}
			tableWidth="100%"
		/>
	</SectionContainer>
);

// Helper component for Documents section
const DocumentsSection = ({
	freelancerProfile,
	t
}: {
	freelancerProfile: FreelancerProfile;
	t: TFunction;
}) => (
	<SectionContainer title={t("freelancerDetails.documents")}>
		<DocumentTable
			documents={freelancerProfile.documents.map(document => ({
				id: document.id,
				name: document.name,
				urlPath: document.urlPath,
				content: document.content,
				type: document.type
			}))}
			displayMode={true}
			showHeader={false}
			tableWidth="100%"
			fullWidth={true}
		/>
	</SectionContainer>
);

// Helper component for Availability section
const AvailabilitySection = ({
	freelancerProfile,
	t
}: {
	freelancerProfile: FreelancerProfile;
	t: TFunction;
}) => (
	<Card
		component={Paper}
		elevation={0}
		sx={{
			margin: "16px",
			borderRadius: "8px"
		}}
	>
		<CardHeader
			title={
				<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
					<Typography variant="h6">
						{t("freelancerDetails.availability.title")}
					</Typography>
				</Box>
			}
		/>
		<Divider />
		<CardContent>
			{!freelancerProfile.availability ||
			(freelancerProfile.availability.isAvailable !== true &&
				freelancerProfile.availability.isAvailable !== false &&
				freelancerProfile.availability.isFullTime !== true &&
				freelancerProfile.availability.isFullTime !== false) ? (
				<Typography
					variant="body1"
					color="text.secondary"
					style={{ fontStyle: "italic", textAlign: "center" }}
				>
					{t("freelancerDetails.notProvided")}
				</Typography>
			) : (
				<Box display="flex" flexDirection="row" gap={1}>
					{/* First line: Chip and Mission Status */}
					{(freelancerProfile.availability.isAvailable === true ||
						freelancerProfile.availability.isAvailable === false) && (
						<Chip
							label={
								!freelancerProfile.availability.isAvailable ? (
									<Typography variant="body2">
										{t("freelancerDetails.availability.onMission", {
											date: formatDateForDisplay(
												freelancerProfile.availability.missionEndDate?.toString(),
												i18n.language,
												"DD/MM/YYYY",
												false
											)
										})}
									</Typography>
								) : (
									<Typography variant="body2">
										{t("freelancerDetails.availability.available")}
									</Typography>
								)
							}
							size="medium"
							variant="outlined"
						/>
					)}
					{(freelancerProfile.availability.isFullTime === true ||
						freelancerProfile.availability.isFullTime === false) && (
						<Chip
							label={
								<Typography variant="body2">
									{freelancerProfile.availability.isFullTime
										? t("freelancerDetails.availability.fullTime")
										: t("freelancerDetails.availability.partTime")}
								</Typography>
							}
							size="medium"
							variant="outlined"
						/>
					)}
					{/* Second line: Available days */}
					{freelancerProfile.availability.daysPerWeek && (
						<Chip
							label={
								<Typography variant="body2">
									{t("freelancerDetails.availability.availableDays", {
										days: freelancerProfile.availability.isAvailable
											? freelancerProfile.availability.daysPerWeek
											: 5 - freelancerProfile.availability.daysPerWeek
									})}
								</Typography>
							}
							size="medium"
							variant="outlined"
						/>
					)}
				</Box>
			)}
		</CardContent>
	</Card>
);

// Helper component for Additional Info section
const AdditionalInfoSection = ({
	freelancerProfile,
	t
}: {
	freelancerProfile: FreelancerProfile;
	t: TFunction;
}) => (
	<Card
		component={Paper}
		elevation={0}
		sx={{
			margin: "16px",
			borderRadius: "8px"
		}}
	>
		<CardHeader
			title={
				<Typography variant="h6">
					{t("freelancerDetails.additionalInformation")}
				</Typography>
			}
		/>
		<Divider />
		<CardContent sx={{ p: 1.5 }}>
			{freelancerProfile.isTrainer === null ||
			freelancerProfile.isTrainer === undefined ? (
				<Typography
					variant="body1"
					color="text.secondary"
					style={{ fontStyle: "italic", textAlign: "center" }}
				>
					{t("freelancerDetails.notProvided")}
				</Typography>
			) : (
				<Box display="flex" flexWrap="wrap" gap={0.5}>
					{(freelancerProfile.isTrainer === true ||
						freelancerProfile.isTrainer === false) && (
						<Chip
							label={
								<Typography variant="body2">
									{t("freelancerDetails.trainer.label")} :{" "}
									{freelancerProfile.isTrainer
										? t("freelancerDetails.trainer.yes")
										: t("freelancerDetails.trainer.no")}
								</Typography>
							}
							size="medium"
							variant="outlined"
						/>
					)}
				</Box>
			)}
		</CardContent>
	</Card>
);

// Helper component for rendering the main freelancer header with avatar and info
const FreelancerMainHeader = ({
	freelancerProfile,
	t,
	evaluationSummary,
	shouldOpenInNewTab = true
}: {
	freelancerProfile: FreelancerProfile;
	t: TFunction;
	evaluationSummary?: FreelancerProfileEvaluationSummary;
	shouldOpenInNewTab?: boolean;
}) => (
	<Box sx={{ p: 3, position: "relative" }}>
		<Stack direction="row" spacing={3} alignItems="center">
			{/* Avatar */}
			<Avatar
				{...stringAvatar(
					freelancerProfile.freelancer?.user?.firstName ?? "",
					freelancerProfile.freelancer?.user?.lastName ?? ""
				)}
				sx={{
					width: 100,
					height: 100,
					fontSize: "3rem"
				}}
				src={`${process.env.REACT_APP_ZOULLOO_FILE_STORAGE_BASE_URL}/${process.env.REACT_APP_FREELANCER_PICTURES_CONTAINER_NAME}/${freelancerProfile.freelancer?.picture?.urlPath}`}
			/>

			{/* Freelancer Info */}
			<Stack spacing={0} flex={1}>
				<Box display="flex" alignItems="center" gap={2}>
					<Typography variant="h1">
						{freelancerProfile.freelancer?.user?.firstName}{" "}
						{freelancerProfile.freelancer?.user?.lastName}
					</Typography>
					{evaluationSummary?.averageRating && evaluationSummary.totalCount > 0 && (
						<Box display="flex" alignItems="center" gap={0.5}>
							<StarRating value={evaluationSummary.averageRating} readOnly size="small" />
							<Typography variant="body2" color="text.secondary">
								({evaluationSummary.totalCount})
							</Typography>
						</Box>
					)}
				</Box>
				<Box ml={0.5}>
					<Typography variant="body1">
						{freelancerProfile.expertise?.label ? (
							t(`Expertises.${freelancerProfile.expertise?.label}`)
						) : (
							<>
								{t("freelancerDetails.expertise")} :
								<Typography variant="body2" fontStyle="italic" component="span">
									{" "}
									({t("common.notProvided")})
								</Typography>
							</>
						)}{" "}
						-{" "}
						{freelancerProfile.experience?.label ? (
							t(`Experiences.${freelancerProfile.experience?.label}`)
						) : (
							<>
								{t("freelancerDetails.experienceLevel")} :
								<Typography variant="body2" fontStyle="italic" component="span">
									{" "}
									({t("common.notProvided")})
								</Typography>
							</>
						)}
					</Typography>
					<Typography variant="body1" color="text.secondary">
						{t("freelancerDetails.freelancerSince")}{" "}
						{freelancerProfile.freelancingYearsOfExperience?.label ? (
							t(
								`FreelancingYearsOfExperiences.${freelancerProfile.freelancingYearsOfExperience?.label}`
							)
						) : (
							<Typography variant="body2" fontStyle="italic" component="span">
								({t("common.notProvided")})
							</Typography>
						)}
					</Typography>
				</Box>
			</Stack>
		</Stack>

		{/* Navigate to freelancer profile edit mode */}
		<NavigationLink
			{...(shouldOpenInNewTab ? { target: "_blank", rel: "noopener noreferrer" } : {})}
			style={{
				position: "absolute",
				top: 0,
				right: 0,
				backgroundColor: zoullooColors.success.main,
				color: zoullooColors.text.white,
				paddingRight: 8,
				paddingLeft: 8,
				paddingTop: 8,
				paddingBottom: 8,
				display: "flex",
				alignItems: "center",
				justifyContent: "center",
				zIndex: 1
			}}
			to={`/profile/${freelancerProfile.freelancer?.key}`}
		>
			<ModeEdit fontSize="large" />
		</NavigationLink>

		{/* Freelancer Status */}
		<Box
			sx={{
				position: "absolute",
				top: 80,
				right: 0,
				bgcolor: zoullooColors.info.main,
				color: zoullooColors.text.white,
				px: 3,
				py: 1.5,
				clipPath: "polygon(20px 0, 100% 0, 100% 100%, 20px 100%, 0 50%)",
				display: "flex",
				alignItems: "center",
				minWidth: "150px",
				justifyContent: "center",
				zIndex: 1
			}}
		>
			<Typography variant="body1" fontWeight="medium">
				{t(
					`enums.FreelancerOnboardingStatus.${
						FreelancerOnboardingStatus[freelancerProfile.onboarding.currentStatus]
					}`
				)}
			</Typography>
		</Box>
	</Box>
);

// Helper component for rendering subheader
const FreelancerSubheader = ({
	renderLocationSection,
	renderPriceSection,
	renderLanguageSection,
	renderEmailSection,
	renderPhoneSection,
	renderSocialIcon,
	renderDocumentIcon,
	isClient
}: {
	renderLocationSection: () => JSX.Element;
	renderPriceSection: () => JSX.Element;
	renderLanguageSection: () => JSX.Element;
	renderEmailSection: () => JSX.Element | null;
	renderPhoneSection: () => JSX.Element | null;
	renderSocialIcon: (
		urlType: UrlType,
		IconComponent: any,
		testId?: string
	) => JSX.Element;
	renderDocumentIcon: (
		documentType: DocumentType,
		IconComponent: any,
		testId?: string
	) => JSX.Element;
	isClient: boolean;
}) => (
	<Grid2
		size={12}
		display="flex"
		flexDirection="column"
		alignItems="center"
		sx={{
			marginX: "12px",
			borderRadius: "8px"
		}}
	>
		{/* First row - Location, Price, Language */}
		<Box sx={{ display: "flex", alignItems: "center", gap: 4, px: 4, mb: 1 }}>
			{renderLocationSection()}
			{renderPriceSection()}
			{renderLanguageSection()}
		</Box>

		{/* Second row - Contact and Icons (hidden for clients) */}
		{!isClient && (
			<Box sx={{ display: "flex", alignItems: "center", gap: 2, px: 4 }}>
				{renderEmailSection()}
				<Divider orientation="vertical" sx={{ width: "1px", height: "16px" }} />
				{renderPhoneSection()}
				<Divider orientation="vertical" sx={{ width: "1px", height: "16px" }} />

				{/* Icon Section */}
				<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
					{renderSocialIcon(UrlType.LinkedInProfessionalProfile, LinkedIn)}
					{renderSocialIcon(UrlType.ProfessionalWebsite, Language)}
					{renderDocumentIcon(DocumentType.Portfolio, FolderOpen)}
					{renderDocumentIcon(DocumentType.Resume, ContactPageOutlined)}
				</Box>
			</Box>
		)}
	</Grid2>
);

// Wrapper component that fetches translations for each experience (needs to be a real React component for hooks)
function TranslatedExperienceWrapper(props: {
	experience: FreelancerProfileProfessionalExperience;
	index: number;
	expandedDescriptions: { [key: number]: boolean };
	descriptionOverflows: { [key: number]: boolean };
	setDescriptionRef: (index: number, el: HTMLElement | null) => void;
	onToggleDescription: (index: number, element?: HTMLElement) => void;
	t: TFunction;
	i18n: any;
	viewingLanguage: LanguageCode;
	sourceLanguage: LanguageCode;
	onLanguageChange: (lang: LanguageCode) => void;
}) {
	const { data: expTranslations } = useEntityTranslations(
		"FreelancerProfileProfessionalExperience",
		props.experience.id,
		props.viewingLanguage.toLowerCase()
	);

	return (
		<ExperienceSection
			{...props}
			experienceTranslations={expTranslations}
		/>
	);
}

// Helper component for rendering experience
const ExperienceSection = ({
	experience,
	index,
	expandedDescriptions,
	descriptionOverflows,
	setDescriptionRef,
	onToggleDescription,
	t,
	i18n,
	viewingLanguage,
	sourceLanguage,
	onLanguageChange,
	experienceTranslations
}: {
	experience: FreelancerProfileProfessionalExperience;
	index: number;
	expandedDescriptions: { [key: number]: boolean };
	descriptionOverflows: { [key: number]: boolean };
	setDescriptionRef: (index: number, el: HTMLElement | null) => void;
	onToggleDescription: (index: number, element?: HTMLElement) => void;
	t: TFunction;
	i18n: any;
	viewingLanguage?: LanguageCode;
	sourceLanguage?: LanguageCode;
	onLanguageChange?: (lang: LanguageCode) => void;
	experienceTranslations?: Record<string, string>;
}) => {
	const getExpField = (fieldName: string, columnValue?: string | null): string => {
		const pascalField = fieldName.charAt(0).toUpperCase() + fieldName.slice(1);
		return resolveTranslatableFieldValue(
			experienceTranslations?.[pascalField],
			columnValue,
			viewingLanguage,
			sourceLanguage ?? LanguageCode.Fr
		);
	};
	return (
		<Box display="flex" gap={2} key={experience.id ?? index} pl={1}>
			{/* Company Logo Placeholder */}
			<Box
				sx={{
					width: 48,
					height: 48,
					backgroundColor: "#f8f9fa",
					borderRadius: "6px",
					// display: "flex",
					display: "none",
					alignItems: "center",
					justifyContent: "center",
					border: "1px solid #e9ecef",
					flexShrink: 0
				}}
			>
				<Typography variant="caption" color="text.disabled">
					{t("freelancerDetails.experience.logoPlaceholder")}
				</Typography>
			</Box>

			{/* Main Content */}
			<Box sx={{ flex: 1, minWidth: 0 }}>
				{viewingLanguage && onLanguageChange && (
					<Box sx={{ display: "flex", justifyContent: "flex-end", mb: 0.5 }}>
						<TranslatableFieldWrapper
							label=""
							editingLanguage={viewingLanguage}
							onLanguageChange={onLanguageChange}
						>
							<></>
						</TranslatableFieldWrapper>
					</Box>
				)}
				<Grid2 container spacing={2} alignItems="flex-start">
					<Grid2 size={6}>
						{/* Company Name and Job Title */}
						<Typography variant="h4" color="text.primary" mb={0.5}>
							{experience.companyName}
						</Typography>
						<Typography variant="body2" color="text.primary" mb={0.5}>
							{getExpField("jobTitle", experience.jobTitle)}
							{experience.jobType && ` - ${t("JobTypes." + experience.jobType.label)}`}
						</Typography>
						<Typography variant="body1" color="text.secondary" mb={1} component={"span"}>
							{t("freelancerDetails.experience.sector")}&nbsp;&nbsp;
							{experience.sector ? (
								<Chip
									key={experience.sector.label}
									label={
										<Typography variant="body2">
											{t(`Sectors.${experience.sector.label}`)}
										</Typography>
									}
									size="small"
									variant="outlined"
									sx={{ mr: 1, mb: 0.2 }}
								/>
							) : (
								<Typography
									variant="body1"
									color="text.secondary"
									style={{ fontStyle: "italic" }}
									component={"span"}
								>
									{t("freelancerDetails.notProvided")}
								</Typography>
							)}
						</Typography>
					</Grid2>
					<Grid2 size={6}>
						{/* Right Side - Dates and Location */}
						<Box
							sx={{
								display: "flex",
								flexDirection: "column",
								alignItems: "flex-end",
								gap: 0.5
							}}
						>
							{/* Date Range and Duration */}
							{(experience.startDate || experience.endDate) && (
								<Typography
									variant="body1"
									color={zoullooColors.grey[550]}
									textAlign="right"
								>
									{formatDateForDisplay(experience.startDate, i18n.language)}{" "}
									{t("freelancerDetails.experience.to")}{" "}
									{experience.isCurrentPosition
										? t("freelancerDetails.experience.today")
										: formatDateForDisplay(experience.endDate, i18n.language)}
									{(() => {
										const duration = calculateDuration(
											experience.startDate,
											experience.endDate,
											experience.isCurrentPosition,
											t,
											"freelancerDetails.experience.duration"
										);
										return duration ? ` - ${duration}` : "";
									})()}
									<br />
									{experience.cityName}
								</Typography>
							)}
						</Box>
					</Grid2>
				</Grid2>

				{/* Description — gated on the VIEWED language, not on the column: the column stays
				    full while the viewed language has nothing, which rendered an empty editor. */}
				{getExpField("missionDescription", experience.missionDescription) ? (
					<HtmlEditor
						ref={(el: HTMLElement | null) => setDescriptionRef(index, el)}
						readonly={true}
						content={getExpField("missionDescription", experience.missionDescription)}
						sx={{
							display: "-webkit-box",
							WebkitLineClamp: expandedDescriptions[index] ? "none" : 2,
							WebkitBoxOrient: "vertical",
							overflow: "hidden",
							textOverflow: "ellipsis"
						}}
					/>
				) : (
					<Typography
						variant="body2"
						color="text.secondary"
						style={{ fontStyle: "italic" }}
					>
						{translatableFieldEmptyLabel(t, viewingLanguage, sourceLanguage ?? LanguageCode.Fr)}
					</Typography>
				)}

				{/* Read More/Less Link - show if content overflows OR is currently expanded */}
				{getExpField("missionDescription", experience.missionDescription) &&
					(descriptionOverflows[index] || expandedDescriptions[index]) && (
						<Link
							component="button"
							variant="caption"
							color="primary"
							onClick={(e: React.MouseEvent<HTMLElement>) => {
								e.preventDefault();
								e.stopPropagation();
								const parentBox = e.currentTarget.closest(
									".MuiBox-root"
								) as HTMLElement | null;
								onToggleDescription(index, parentBox || undefined);
							}}
							sx={{
								textDecoration: "none",
								fontWeight: "medium",
								cursor: "pointer",
								border: "none",
								background: "none",
								padding: 0,
								"&:hover": {
									textDecoration: "underline"
								},
								display: "block",
								mt: 0.5,
								fontSize: "15px",
								width: "fit-content"
							}}
						>
							{expandedDescriptions[index]
								? t("freelancerDetails.experience.readLess")
								: t("freelancerDetails.experience.readMore")}
						</Link>
					)}
			</Box>
		</Box>
	);
};

// Helper component for rendering languages section
const LanguagesSection = ({
	title,
	maternal,
	current,
	operational,
	t
}: {
	title: string;
	maternal: string[];
	current: string[];
	operational: string[];
	t: TFunction;
}) => (
	<Card
		component={Paper}
		elevation={0}
		sx={{
			margin: "16px",
			borderRadius: "8px"
		}}
	>
		<CardHeader title={<Typography variant="h6">{t(title)}</Typography>} />
		<Divider />
		<CardContent sx={{ p: 1.5 }}>
			{maternal.length === 0 && current.length === 0 && operational.length === 0 ? (
				<Typography
					variant="body1"
					color="text.secondary"
					style={{ fontStyle: "italic", textAlign: "center" }}
				>
					{t("freelancerDetails.notProvided")}
				</Typography>
			) : (
				<Grid2 container spacing={2}>
					<Grid2 size={4}>
						<Typography
							variant="body1"
							color="text.secondary"
							mb={2}
							display="block"
							fontSize={"0.95rem"}
						>
							{t(`LanguageProficiencies.${LanguageProficiency.Native}`)}
						</Typography>
						{maternal.length === 0 ? (
							<Typography
								variant="body1"
								color="text.secondary"
								style={{ fontStyle: "italic", textAlign: "center" }}
							>
								{t("freelancerDetails.notProvided")}
							</Typography>
						) : (
							<Box display="flex" flexWrap="wrap" gap={0.5}>
								{maternal.map(item => (
									<Chip
										key={item}
										label={<Typography variant="body2">{item}</Typography>}
										size="medium"
										variant="outlined"
									/>
								))}
							</Box>
						)}
					</Grid2>
					<Grid2 size={4}>
						<Typography
							variant="body1"
							color="text.secondary"
							mb={2}
							display="block"
							fontSize={"0.95rem"}
						>
							{t(`LanguageProficiencies.${LanguageProficiency.Fluent}`)}
						</Typography>
						{current.length === 0 ? (
							<Typography
								variant="body1"
								color="text.secondary"
								style={{ fontStyle: "italic", textAlign: "center" }}
							>
								{t("freelancerDetails.notProvided")}
							</Typography>
						) : (
							<Box display="flex" flexWrap="wrap" gap={0.5}>
								{current.map(item => (
									<Chip
										key={item}
										label={<Typography variant="body2">{item}</Typography>}
										size="medium"
										variant="outlined"
									/>
								))}
							</Box>
						)}
					</Grid2>
					<Grid2 size={4}>
						<Typography
							variant="body1"
							color="text.secondary"
							mb={2}
							display="block"
							fontSize={"0.95rem"}
						>
							{t(`LanguageProficiencies.${LanguageProficiency.Operational}`)}
						</Typography>
						{operational.length === 0 ? (
							<Typography
								variant="body1"
								color="text.secondary"
								style={{ fontStyle: "italic", textAlign: "center" }}
							>
								{t("freelancerDetails.notProvided")}
							</Typography>
						) : (
							<Box display="flex" flexWrap="wrap" gap={0.5}>
								{operational.map(item => (
									<Chip
										key={item}
										label={<Typography variant="body2">{item}</Typography>}
										size="medium"
										variant="outlined"
									/>
								))}
							</Box>
						)}
					</Grid2>
				</Grid2>
			)}
		</CardContent>
	</Card>
);

// Helper component for rendering Last Exchange section
const LastExchangeSection = ({
	isLoadingNote,
	isNoteError,
	latestNote,
	currentUser,
	refetchLatestNote,
	latestNoteExpanded,
	latestNoteOverflows,
	setLatestNoteRef,
	onToggleLatestNote,
	t,
	i18n
}: {
	isLoadingNote: boolean;
	isNoteError: boolean;
	latestNote: ApplicationNote | null | undefined;
	currentUser: User | null | undefined;
	refetchLatestNote: () => void;
	latestNoteExpanded: boolean;
	latestNoteOverflows: boolean;
	setLatestNoteRef: (el: HTMLElement | null) => void;
	onToggleLatestNote: (element?: HTMLElement) => void;
	t: TFunction;
	i18n: any;
}) => {
	if (isLoadingNote) {
		return <LastApplicationNoteTakenSkeleton />;
	}

	if (isNoteError) {
		return <LastApplicationNoteTakenErrorDisplay onRetry={refetchLatestNote} />;
	}

	if (!latestNote) {
		return <LastApplicationNoteTakenNotFound />;
	}

	return (
		<Card
			component={Paper}
			elevation={0}
			sx={{
				margin: "16px",
				borderRadius: "8px"
			}}
		>
			<CardHeader
				title={
					<>
						<Typography variant="h6">
							{t("freelancerDetails.lastExchange.title")}
						</Typography>
						<Typography variant="body2">
							{t("freelancerDetails.lastExchange.subtitle", {
								date: formatDateForDisplay(
									latestNote.createdAt.toString(),
									i18n.language,
									"DD/MM/YYYY",
									false
								),
								firstName: currentUser?.firstName || "",
								lastName: currentUser?.lastName || ""
							})}
						</Typography>
						<Typography variant="body2" color="text.secondary">
							{t(`NoteSubjects.${latestNote.subject.label}`, latestNote.subject.label)}
						</Typography>
					</>
				}
			/>
			<Divider />
			<CardContent>
				<HtmlEditor
					ref={setLatestNoteRef}
					readonly={true}
					content={latestNote.content}
					sx={{
						display: "-webkit-box",
						WebkitLineClamp: latestNoteExpanded ? "none" : 3,
						WebkitBoxOrient: "vertical",
						overflow: "hidden",
						textOverflow: "ellipsis"
					}}
				/>
				{(latestNoteOverflows || latestNoteExpanded) && (
					<Link
						component="button"
						color="primary"
						onClick={(e: React.MouseEvent<HTMLElement>) => {
							e.preventDefault();
							e.stopPropagation();
							const parentCard = e.currentTarget.closest(
								".MuiCard-root"
							) as HTMLElement | null;
							onToggleLatestNote(parentCard || undefined);
						}}
						sx={{
							textDecoration: "none",
							fontWeight: "medium",
							cursor: "pointer",
							border: "none",
							background: "none",
							padding: 0,
							"&:hover": {
								textDecoration: "underline"
							},
							display: "block",
							mt: 0.5,
							fontSize: "15px",
							width: "fit-content"
						}}
					>
						{latestNoteExpanded
							? t("freelancerDetails.experience.readLess")
							: t("freelancerDetails.experience.readMore")}
					</Link>
				)}
			</CardContent>
		</Card>
	);
};

export default function FreelancerDetails({
	freelancerProfileId,
	isExpanded,
	freelancerProfileKey = "",
	hideLastExchange = false,
	showSocialNetworks = false,
	showDocuments = false,
	showAvailability = false,
	showAdditionalInfo = false,
	showContact = false,
	showNotes = false,
	showHeader = false,
	showEvaluations = false,
	presentationActions
}: FreelancerDetailsProps) {
	const { t, i18n } = useTranslation();
	const { shouldOpenInNewTab } = useEnvironmentNavigation();
	const { currentLanguage } = useLanguage();
	const [viewingLanguage, setViewingLanguage] = useState<LanguageCode>(currentLanguage);

	// Sync if global language changes
	useEffect(() => {
		setViewingLanguage(currentLanguage);
	}, [currentLanguage]);

	const {
		data: freelancerProfile,
		isLoading,
		isRefetching,
		isError,
		refetch
	} = useFreelancerProfile(freelancerProfileId, isExpanded, freelancerProfileKey);

	// The language the talent WRITES in. Their cells in it are the hand-written source, and it is
	// the only language guaranteed to hold content: the other one exists only once the worker has
	// produced it. Falls back to the platform primary while the profile is still loading.
	const talentSourceLanguage =
		freelancerProfile?.freelancer?.user?.settings?.preferredLanguage ?? LanguageCode.Fr;

	// Fetch translations for the presentation field
	const { data: profileTranslations } = useEntityTranslations(
		"FreelancerProfile",
		freelancerProfile?.id,
		viewingLanguage.toLowerCase()
	);

	const getTranslatedField = (fieldName: string, columnValue?: string | null): string => {
		const pascalField = fieldName.charAt(0).toUpperCase() + fieldName.slice(1);
		return resolveTranslatableFieldValue(
			profileTranslations?.[pascalField],
			columnValue,
			viewingLanguage,
			talentSourceLanguage
		);
	};

	const emptyFieldLabel = (): string =>
		translatableFieldEmptyLabel(t, viewingLanguage, talentSourceLanguage);

	// Get the current user TAM for application notes functionality
	const { data: currentUser } = useCurrentUser();

	// Get evaluation summary for the freelancer profile (use loaded profile ID as fallback when freelancerProfileId is 0)
	const resolvedProfileId = freelancerProfile?.id || freelancerProfileId;
	const { data: evaluationSummary } = useFreelancerProfileEvaluations(resolvedProfileId, isExpanded && resolvedProfileId > 0, EvaluatorType.Client);
	const { data: freelancerEvaluationSummary } = useFreelancerProfileEvaluations(resolvedProfileId, isExpanded && resolvedProfileId > 0, EvaluatorType.Freelancer);

	const [evaluationTabIndex, setEvaluationTabIndex] = useState(0);

	// Get the latest note created by the current user
	const {
		data: latestNote,
		isLoading: isLoadingNote,
		isError: isNoteError,
		refetch: refetchLatestNote
	} = useLatestNoteOfSpecificApplication(
		freelancerProfile?.onboarding.id ?? 0,
		Boolean(freelancerProfile?.onboarding.id) &&
			currentUser?.roles?.includes(UserRole.TAM)
	);

	// Use custom hook to prepare data (must be called before early returns)
	const {
		sectors,
		businessDomains,
		skills,
		tools,
		languages,
		regions,
		preferences,
		professionalExperiences
	} = useFreelancerData(freelancerProfile, t);

	// State for managing expanded descriptions
	const [expandedDescriptions, setExpandedDescriptions] = useState<{
		[key: number]: boolean;
	}>({});
	const [descriptionOverflows, setDescriptionOverflows] = useState<{
		[key: number]: boolean;
	}>({});

	// State for managing presentation text expansion
	const [presentationExpanded, setPresentationExpanded] = useState(false);
	const [presentationOverflows, setPresentationOverflows] = useState(false);

	// State for managing latest note expansion
	const [latestNoteExpanded, setLatestNoteExpanded] = useState(false);
	const [latestNoteOverflows, setLatestNoteOverflows] = useState(false);

	// State for managing copy status
	const [emailCopied, setEmailCopied] = useState(false);
	const [phoneCopied, setPhoneCopied] = useState(false);

	// Ref to track which elements have been processed to avoid infinite loops
	const processedElements = useRef<Set<string>>(new Set());

	// Function to copy email to clipboard
	const copyEmail = useCallback(async () => {
		if (freelancerProfile?.freelancer?.user.email) {
			try {
				await navigator.clipboard.writeText(freelancerProfile.freelancer.user.email);
				setEmailCopied(true);
				setTimeout(() => setEmailCopied(false), 2000); // Reset after 2 seconds
			} catch (err) {
				console.error("Failed to copy email: ", err);
			}
		}
	}, [freelancerProfile?.freelancer?.user.email]);

	// Function to copy phone to clipboard
	const copyPhone = useCallback(async () => {
		if (freelancerProfile?.freelancer?.phoneNumber) {
			try {
				await navigator.clipboard.writeText(freelancerProfile.freelancer.phoneNumber);
				setPhoneCopied(true);
				setTimeout(() => setPhoneCopied(false), 2000); // Reset after 2 seconds
			} catch (err) {
				console.error("Failed to copy phone: ", err);
			}
		}
	}, [freelancerProfile?.freelancer?.phoneNumber]);

	// Helper functions for header rendering (moved to top to avoid conditional hook calls)
	const renderLocationSection = useCallback(
		() => (
			<Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
				<LocationOn fontSize="small" color="action" />
				{freelancerProfile?.freelancer?.address?.city &&
				freelancerProfile?.freelancer?.address?.countryCode ? (
					<Typography variant="body2">
						{freelancerProfile.freelancer.address.city} (
						{freelancerProfile.freelancer.address.countryCode})
					</Typography>
				) : (
					<Typography variant="body2" fontStyle="italic">
						{t("freelancerDetails.notProvided")}
					</Typography>
				)}
			</Box>
		),
		[freelancerProfile?.freelancer?.address, t]
	);

	const renderPriceSection = useCallback(
		() => (
			<Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
				<Euro fontSize="small" color="action" />
				{freelancerProfile?.dailyRateMin && freelancerProfile?.dailyRateMax ? (
					<Typography variant="body2">
						{freelancerProfile.dailyRateMin} - {freelancerProfile.dailyRateMax}
					</Typography>
				) : (
					<Typography variant="body2" fontStyle="italic">
						{t("freelancerDetails.notProvided")}
					</Typography>
				)}
			</Box>
		),
		[freelancerProfile?.dailyRateMin, freelancerProfile?.dailyRateMax, t]
	);

	const renderLanguageSection = useCallback(
		() => (
			<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
				<RecordVoiceOverOutlined fontSize="small" color="action" />
				{freelancerProfile?.freelancer?.languages?.some(
					lang =>
						lang.languageProficiency === LanguageProficiency.Native ||
						lang.languageProficiency === LanguageProficiency.Fluent
				) === true ? (
					<Typography variant="body2">
						{freelancerProfile.freelancer.languages
							.filter(
								lang =>
									lang.languageProficiency === LanguageProficiency.Native ||
									lang.languageProficiency === LanguageProficiency.Fluent
							)
							.map(lang => t(`Languages.${lang.language.label}`))
							?.join(" | ")}
					</Typography>
				) : (
					<Typography variant="body2" fontStyle="italic">
						{t("freelancerDetails.notProvided")}
					</Typography>
				)}
			</Box>
		),
		[freelancerProfile?.freelancer?.languages, t]
	);

	const renderCopyButton = useCallback(
		(isCopied: boolean, onCopy: () => void) => (
			<IconButton
				disableRipple
				onClick={onCopy}
				sx={{
					width: 20,
					height: 20,
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
					position: "relative"
				}}
			>
				<Fade in={!isCopied} timeout={300}>
					<ContentCopyOutlined
						sx={{
							fontSize: "15px",
							position: "absolute"
						}}
					/>
				</Fade>
				<Fade in={isCopied} timeout={300}>
					<Check
						sx={{
							fontSize: "15px",
							position: "absolute"
						}}
					/>
				</Fade>
			</IconButton>
		),
		[]
	);

	const renderEmailSection = useCallback(() => {
		// Hide email section for clients
		if (currentUser?.type === UserType.Client) {
			return null;
		}

		const hasEmail = freelancerProfile?.freelancer?.user.email;
		return (
			<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
				<Email fontSize="small" color={hasEmail ? "action" : "disabled"} />
				<Link
					sx={{
						color: hasEmail ? zoullooColors.secondary : "text.disabled",
						textDecoration: hasEmail ? "underline" : "none",
						cursor: hasEmail ? "pointer" : "default",
						"&:hover": {
							textDecoration: hasEmail ? "underline" : "none"
						}
					}}
					href={hasEmail ? `mailto:${hasEmail}` : undefined}
					onClick={!hasEmail ? e => e.preventDefault() : undefined}
				>
					<Typography variant="body2">
						{hasEmail || t("freelancerDetails.header.sendMessage")}
					</Typography>
				</Link>
				{hasEmail && renderCopyButton(emailCopied, copyEmail)}
			</Box>
		);
	}, [
		currentUser?.type,
		freelancerProfile?.freelancer?.user.email,
		emailCopied,
		copyEmail,
		renderCopyButton,
		t
	]);

	const renderPhoneSection = useCallback(() => {
		// Hide phone section for clients
		if (currentUser?.type === UserType.Client) {
			return null;
		}

		const hasPhone = freelancerProfile?.freelancer?.phoneNumber;
		return (
			<Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
				<Phone fontSize="small" color={hasPhone ? "action" : "disabled"} />
				<Link
					sx={{
						textDecoration: hasPhone ? "underline" : "none",
						color: hasPhone ? zoullooColors.secondary : "text.disabled",
						cursor: hasPhone ? "pointer" : "default",
						"&:hover": {
							textDecoration: hasPhone ? "underline" : "none"
						}
					}}
					href={hasPhone ? `tel:${hasPhone}` : undefined}
					onClick={!hasPhone ? e => e.preventDefault() : undefined}
				>
					<Typography variant="body2">{hasPhone || t("freelancerDetails.header.call")}</Typography>
				</Link>
				{hasPhone && renderCopyButton(phoneCopied, copyPhone)}
			</Box>
		);
	}, [
		currentUser?.type,
		freelancerProfile?.freelancer?.phoneNumber,
		phoneCopied,
		copyPhone,
		renderCopyButton,
		t
	]);

	const getUrlByType = useCallback(
		(urlType: UrlType) => {
			return freelancerProfile?.urls.find(url => url.type?.label === urlType)?.url;
		},
		[freelancerProfile?.urls]
	);

	// Generic function to render clickable icons with URLs
	const renderClickableIcon = useCallback(
		(IconComponent: any, url: string | undefined, testId?: string) => {
			const hasUrl = Boolean(url);

			return (
				<Link
					sx={{
						width: 30,
						height: 30,
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						cursor: hasUrl ? "pointer" : "default"
					}}
					href={hasUrl ? url : undefined}
					target={hasUrl ? "_blank" : undefined}
					rel={hasUrl ? "noopener noreferrer" : undefined}
					onClick={!hasUrl ? e => e.preventDefault() : undefined}
					data-testid={testId}
				>
					<IconComponent color={hasUrl ? "action" : "disabled"} fontSize="medium" />
				</Link>
			);
		},
		[]
	);

	const renderSocialIcon = useCallback(
		(urlType: UrlType, IconComponent: any, testId?: string) => {
			const url = getUrlByType(urlType);
			return renderClickableIcon(IconComponent, url, testId);
		},
		[getUrlByType, renderClickableIcon]
	);

	const getDocumentUrl = useCallback(
		(document?: FreelancerProfileDocument): string | undefined => {
			if (!document) return undefined;

			const baseUrl = process.env.REACT_APP_ZOULLOO_FILE_STORAGE_BASE_URL;
			const containerName =
				process.env.REACT_APP_FREELANCER_PROFILE_DOCUMENTS_CONTAINER_NAME;

			return `${baseUrl}/${containerName}/${document.urlPath}`;
		},
		[]
	);

	const renderDocumentIcon = useCallback(
		(documentType: DocumentType, IconComponent: any, testId?: string) => {
			const document = freelancerProfile?.documents.find(
				doc => doc.type?.label === documentType
			);
			const url = getDocumentUrl(document);
			return renderClickableIcon(IconComponent, url, testId);
		},
		[freelancerProfile?.documents, getDocumentUrl, renderClickableIcon]
	);

	// Function to toggle description expansion
	const onToggleDescription = useCallback((index: number, element?: HTMLElement) => {
		setExpandedDescriptions(prev => ({
			...prev,
			[index]: !prev[index]
		}));

		// Scroll to element after state update to prevent jump
		if (element) {
			setTimeout(() => {
				element.scrollIntoView({ behavior: "smooth", block: "nearest" });
			}, 100);
		}
	}, []);

	// Function to toggle presentation expansion
	const onTogglePresentation = useCallback((element?: HTMLElement) => {
		setPresentationExpanded(prev => !prev);

		// Scroll to element after state update to prevent jump
		if (element) {
			setTimeout(() => {
				element.scrollIntoView({ behavior: "smooth", block: "nearest" });
			}, 100);
		}
	}, []);

	// Function to set description ref and check for overflow
	const setDescriptionRef = useCallback(
		(index: number, el: HTMLElement | null) => {
			if (el) {
				const elementId = `${freelancerProfileId}-${index}`;

				// Only process if we haven't already processed this element
				if (!processedElements.current.has(elementId)) {
					processedElements.current.add(elementId);

					// Use requestAnimationFrame to ensure DOM has been updated
					requestAnimationFrame(() => {
						const hasOverflow = el.scrollHeight > el.clientHeight;
						setDescriptionOverflows(prev => ({
							...prev,
							[index]: hasOverflow
						}));
					});
				}
			}
		},
		[freelancerProfileId]
	);

	// Function to set presentation ref and check for overflow
	const setPresentationRef = useCallback(
		(el: HTMLElement | null) => {
			if (el) {
				const elementId = `presentation-${freelancerProfileId}`;

				// Only process if we haven't already processed this element
				if (!processedElements.current.has(elementId)) {
					processedElements.current.add(elementId);

					// Use requestAnimationFrame to ensure DOM has been updated
					requestAnimationFrame(() => {
						const hasOverflow = el.scrollHeight > el.clientHeight;
						setPresentationOverflows(hasOverflow);
					});
				}
			}
		},
		[freelancerProfileId]
	);

	// Function to toggle latest note expansion
	const onToggleLatestNote = useCallback((element?: HTMLElement) => {
		setLatestNoteExpanded(prev => !prev);

		// Scroll to element after state update to prevent jump
		if (element) {
			setTimeout(() => {
				element.scrollIntoView({ behavior: "smooth", block: "nearest" });
			}, 100);
		}
	}, []);

	// Function to set latest note ref and check for overflow
	const setLatestNoteRef = useCallback(
		(el: HTMLElement | null) => {
			if (el) {
				const elementId = `latest-note-${freelancerProfileId}`;

				// Only process if we haven't already processed this element
				if (!processedElements.current.has(elementId)) {
					processedElements.current.add(elementId);

					// Use requestAnimationFrame to ensure DOM has been updated
					requestAnimationFrame(() => {
						const hasOverflow = el.scrollHeight > el.clientHeight;
						setLatestNoteOverflows(hasOverflow);
					});
				}
			}
		},
		[freelancerProfileId]
	);

	if (isError) {
		return <ErrorDisplay onRetry={() => refetch()} />;
	}

	if (isLoading || isRefetching) {
		return <FreelancerDetailsSkeleton />;
	}

	if (!freelancerProfile) {
		return <FreelancerNotFound />;
	}

	return (
		<Grid2 container spacing={3}>
			{/* Main Header with Avatar and Status (conditional) */}
			{showHeader && (
				<Grid2 size={12}>
					<FreelancerMainHeader freelancerProfile={freelancerProfile} t={t} evaluationSummary={evaluationSummary} shouldOpenInNewTab={shouldOpenInNewTab} />
				</Grid2>
			)}

			{/* Header Navigation Bar */}
			<FreelancerSubheader
				renderLocationSection={renderLocationSection}
				renderPriceSection={renderPriceSection}
				renderLanguageSection={renderLanguageSection}
				renderEmailSection={renderEmailSection}
				renderPhoneSection={renderPhoneSection}
				renderSocialIcon={renderSocialIcon}
				renderDocumentIcon={renderDocumentIcon}
				isClient={currentUser?.type === UserType.Client}
			/>

			{/* Left Column */}
			<Grid2 size={12}>
				{/* Last Exchange - Only show if current user is TAM and not hidden */}
				{currentUser?.roles?.includes(UserRole.TAM) && !hideLastExchange && (
					<LastExchangeSection
						isLoadingNote={isLoadingNote}
						isNoteError={isNoteError}
						latestNote={latestNote}
						currentUser={currentUser}
						refetchLatestNote={refetchLatestNote}
						latestNoteExpanded={latestNoteExpanded}
						latestNoteOverflows={latestNoteOverflows}
						setLatestNoteRef={setLatestNoteRef}
						onToggleLatestNote={onToggleLatestNote}
						t={t}
						i18n={i18n}
					/>
				)}
				{/* Profile title */}
				<Card
					component={Paper}
					elevation={0}
					sx={{
						margin: "16px",
						borderRadius: "8px"
					}}
				>
					<CardContent>
						<TranslatableFieldWrapper
							label={t("freelancerDetails.profileTitle")}
							editingLanguage={viewingLanguage}
							onLanguageChange={setViewingLanguage}
						>
							<Typography
								variant="body1"
								color="text.secondary"
								textAlign={"justify"}
								style={
									!getTranslatedField("title", freelancerProfile.title)
										? { fontStyle: "italic", textAlign: "center" }
										: {}
								}
							>
								{getTranslatedField("title", freelancerProfile.title) || emptyFieldLabel()}
							</Typography>
						</TranslatableFieldWrapper>
					</CardContent>
				</Card>
				{/* Profile Presentation */}
				<Card
					component={Paper}
					elevation={0}
					sx={{
						margin: "16px",
						borderRadius: "8px"
					}}
				>
					<CardContent>
						<TranslatableFieldWrapper
							label={t("freelancerDetails.presentation")}
							editingLanguage={viewingLanguage}
							onLanguageChange={setViewingLanguage}
						>
						{!getTranslatedField("presentation", freelancerProfile.presentation) ? (
							<Typography
								variant="body1"
								color="text.secondary"
								style={{ fontStyle: "italic", textAlign: "center" }}
							>
								{emptyFieldLabel()}
							</Typography>
						) : (
							<>
								<HtmlEditor
									ref={setPresentationRef}
									readonly={true}
									content={getTranslatedField("presentation", freelancerProfile.presentation)!}
									sx={{
										display: "-webkit-box",
										WebkitLineClamp: presentationExpanded ? "none" : 3,
										WebkitBoxOrient: "vertical",
										overflow: "hidden",
										textOverflow: "ellipsis"
									}}
								/>

								{/* Read More/Less Link - show if content overflows OR is currently expanded */}
								{(presentationOverflows || presentationExpanded) && (
									<Link
										component="button"
										variant="caption"
										color="primary"
										onClick={(e: React.MouseEvent<HTMLElement>) => {
											e.preventDefault();
											e.stopPropagation();
											const parentCard = e.currentTarget.closest(
												".MuiCard-root"
											) as HTMLElement | null;
											onTogglePresentation(parentCard || undefined);
										}}
										sx={{
											textDecoration: "none",
											fontWeight: "medium",
											cursor: "pointer",
											border: "none",
											background: "none",
											padding: 0,
											"&:hover": {
												textDecoration: "underline"
											},
											display: "block",
											mt: 1,
											fontSize: "15px",
											width: "fit-content"
										}}
									>
										{presentationExpanded
											? t("freelancerDetails.experience.readLess")
											: t("freelancerDetails.experience.readMore")}
									</Link>
								)}
							</>
						)}
						</TranslatableFieldWrapper>
						{presentationActions}
					</CardContent>
				</Card>
				{/* Sectors and Domains */}
				<SectorsAndDomainsSection sectors={sectors} domains={businessDomains} t={t} />
				{/* Competences */}
				<SkillsSection
					title="freelancerDetails.sections.skills"
					expertises={skills.expertises}
					confirmed={skills.confirmed}
					t={t}
				/>
				{/* Tools */}
				<ToolsSection
					title="freelancerDetails.sections.tools"
					mastered={tools.mastered}
					used={tools.used}
					t={t}
				/>
				{/* Languages */}
				<LanguagesSection
					title="freelancerDetails.sections.languages"
					maternal={languages.maternal}
					current={languages.fluent}
					operational={languages.operational}
					t={t}
				/>
				{/* Geographic Zones */}
				<ChipListSection
					items={regions}
					label="freelancerDetails.sections.geographicZones"
					t={t}
				/>
				{/* Social Media */}
				{showSocialNetworks &&
					freelancerProfile?.urls &&
					freelancerProfile.urls.length > 0 && (
						<OnlinePresenceSection freelancerProfile={freelancerProfile} t={t} />
					)}
				{/* Documents */}
				{showDocuments &&
					freelancerProfile?.documents &&
					freelancerProfile.documents.length > 0 && (
						<DocumentsSection freelancerProfile={freelancerProfile} t={t} />
					)}
				{/* Availability */}
				{showAvailability && (
					<AvailabilitySection freelancerProfile={freelancerProfile} t={t} />
				)}
				{/* Preferences */}
				<ChipListSection
					items={preferences}
					label="freelancerDetails.sections.preferences"
					t={t}
				/>
				{/* Informations complémentaires */}
				{showAdditionalInfo && (
					<AdditionalInfoSection freelancerProfile={freelancerProfile} t={t} />
				)}
				<Card
					component={Paper}
					elevation={0}
					sx={{
						margin: "16px",
						borderRadius: "8px"
					}}
				>
					<CardHeader
						title={
							<Typography variant="h6">
								{t("freelancerDetails.sections.experiences")}
							</Typography>
						}
					/>
					<Divider />
					<CardContent sx={{ p: 1.5 }}>
						{professionalExperiences.length === 0 ? (
							<Typography
								variant="body1"
								color="text.secondary"
								style={{ fontStyle: "italic", textAlign: "center" }}
							>
								{t("freelancerDetails.notProvided")}
							</Typography>
						) : (
							professionalExperiences.map(
								(experience: FreelancerProfileProfessionalExperience, index: number) => (
									<Box key={experience.id || index}>
										<TranslatedExperienceWrapper
											experience={experience}
											index={index}
											expandedDescriptions={expandedDescriptions}
											descriptionOverflows={descriptionOverflows}
											setDescriptionRef={setDescriptionRef}
											onToggleDescription={onToggleDescription}
											t={t}
											i18n={i18n}
											viewingLanguage={viewingLanguage}
											sourceLanguage={talentSourceLanguage}
											onLanguageChange={setViewingLanguage}
										/>
										{index < professionalExperiences.length - 1 && (
											<Divider sx={{ marginY: 4, marginX: "auto", width: "90%" }} />
										)}
									</Box>
								)
							)
						)}
					</CardContent>
				</Card>
				{/* Contact Section */}
				{showContact && (
					<Card
						component={Paper}
						elevation={0}
						sx={{
							margin: "16px",
							borderRadius: "8px"
						}}
					>
						<CardHeader
							title={
								<Typography variant="h6">
									{t("freelancerDetails.contact.title")}
								</Typography>
							}
						/>
						<Divider />
						<CardContent sx={{ p: "0 !important" }}>
							<Grid2 container spacing={0}>
								{/* Personal information */}
								<Grid2 size={6}>
									<Card
										elevation={0}
										sx={{
											border: 0,
											boxShadow: "none",
											height: "100%"
										}}
									>
										<CardHeader
											title={
												<Typography variant="subtitle1" fontWeight="medium">
													{t("freelancerDetails.contact.personalInformation")}
												</Typography>
											}
											sx={{ pb: 1, mb: 0.48 }}
										/>
										<Divider />
										<CardContent sx={{ pt: 2 }}>
											<Box sx={{ display: "flex", flexDirection: "column" }}>
												{/* Contact */}
												<Typography variant="body2" color="text.secondary">
													{t("freelancerDetails.contact.fullName")}
												</Typography>
												<Box sx={{ mb: 1.3 }}>
													<Typography variant="body2">
														{freelancerProfile.freelancer?.user.firstName || ""}{" "}
														{freelancerProfile.freelancer?.user.lastName || ""}
													</Typography>
												</Box>
												{/* Hide phone and email for clients */}
												{currentUser?.type !== UserType.Client && (
													<>
														<Typography variant="body2" color="text.secondary">
															{t("freelancerDetails.contact.phoneNumber")}
														</Typography>
														<Box sx={{ mb: 1.3 }}>
															<Typography variant="body2">
																{freelancerProfile.freelancer?.phoneNumber ||
																	t("freelancerDetails.notProvided")}
															</Typography>
														</Box>
														<Typography variant="body2" color="text.secondary">
															{t("freelancerDetails.contact.email")}
														</Typography>
														<Box sx={{ mb: 1.3 }}>
															<Typography variant="body2">
																{freelancerProfile.freelancer?.user.email ||
																	t("freelancerDetails.notProvided")}
															</Typography>
														</Box>
													</>
												)}
												{/* Address */}
												<Typography variant="body2" color="text.secondary">
													{t("freelancerDetails.contact.address")}
												</Typography>
												<Box sx={{ mb: 1.3 }}>
													{freelancerProfile.freelancer?.address ? (
														<Typography variant="body2">
															{freelancerProfile.freelancer.address.fullAddress || ""}
														</Typography>
													) : (
														<Typography variant="body2">
															{t("freelancerDetails.notProvided")}
														</Typography>
													)}
												</Box>
											</Box>
										</CardContent>
									</Card>
								</Grid2>

								{/* Professional information */}
								<Grid2 size={6}>
									<Card
										elevation={0}
										sx={{
											border: 0,
											borderRadius: 0,
											borderLeft: `1px solid ${zoullooColors.grey[400]}`,
											boxShadow: "none",
											height: "100%"
										}}
									>
										<CardHeader
											title={
												<Box
													sx={{
														display: "flex",
														flexDirection: "row",
														justifyContent: "space-between",
														alignItems: "center"
													}}
												>
													<Typography variant="subtitle1" fontWeight="medium">
														{t("freelancerDetails.contact.professionalInformation")}
													</Typography>
													{freelancerProfile.company?.isPortageSalarial && (
														<Chip
															label={
																<Typography variant="body2">
																	{t("freelancerDetails.contact.portageSalarial")}
																</Typography>
															}
															size="medium"
															variant="outlined"
														/>
													)}
												</Box>
											}
											sx={{ pb: 1 }}
										/>
										<Divider />
										<CardContent sx={{ pt: 2 }}>
											<Box sx={{ display: "flex", flexDirection: "column" }}>
												{/* Legal Name */}
												<Typography variant="body2" color="text.secondary">
													{t("freelancerDetails.company.legalName")}
												</Typography>
												<Box sx={{ mb: 1.3 }}>
													<Typography variant="body2">
														{freelancerProfile.company?.legalName ||
															t("freelancerDetails.notProvided")}
													</Typography>
												</Box>
												{/* Professional Address */}
												<Typography variant="body2" color="text.secondary">
													{t("freelancerDetails.company.address")}
												</Typography>
												<Box sx={{ mb: 1.3 }}>
													{freelancerProfile.company?.address ? (
														<Typography variant="body2">
															{freelancerProfile.company.address.fullAddress || ""}
														</Typography>
													) : (
														<Typography variant="body2">
															{t("freelancerDetails.notProvided")}
														</Typography>
													)}
												</Box>

												{/* Identification Number */}
												<Typography variant="body2" color="text.secondary">
													{t("freelancerDetails.company.identificationNumber")}
												</Typography>
												<Box sx={{ mb: 1.3 }}>
													<Typography variant="body2">
														{freelancerProfile.company?.identificationNumber || ""}
													</Typography>
												</Box>

												{/* Company type */}
												<Typography variant="body2" color="text.secondary">
													{t("freelancerDetails.company.vatNumber")}
												</Typography>
												<Box sx={{ mb: 1.3 }}>
													{freelancerProfile.company?.vatNumber ? (
														<Typography variant="body2">
															{freelancerProfile.company.vatNumber || ""}
														</Typography>
													) : (
														<Typography variant="body2">
															{t("freelancerDetails.notProvided")}
														</Typography>
													)}
												</Box>

												{/* Professional Contact */}
												<Typography variant="body2" color="text.secondary">
													{t("freelancerDetails.company.fullName")}
												</Typography>
												<Box sx={{ mb: 1.3 }}>
													<Typography variant="body2">
														{freelancerProfile.company?.contactFirstName || ""}{" "}
														{freelancerProfile.company?.contactLastName || ""}
													</Typography>
												</Box>
												<Typography variant="body2" color="text.secondary">
													{t("freelancerDetails.company.phoneNumber")}
												</Typography>
												<Box sx={{ mb: 1.3 }}>
													<Typography variant="body2">
														{freelancerProfile.company?.contactPhoneNumber ||
															t("freelancerDetails.notProvided")}
													</Typography>
												</Box>
												<Typography variant="body2" color="text.secondary">
													{t("freelancerDetails.company.email")}
												</Typography>
												<Box sx={{ mb: 1.3 }}>
													<Typography variant="body2">
														{freelancerProfile.company?.contactEmail ||
															t("freelancerDetails.notProvided")}
													</Typography>
												</Box>
											</Box>
										</CardContent>
									</Card>
								</Grid2>
							</Grid2>
						</CardContent>
					</Card>
				)}
				{/* Evaluations Section */}
				{showEvaluations &&
					(currentUser?.type === UserType.Internal || currentUser?.type === UserType.Client) && (
					<Card
						component={Paper}
						elevation={0}
						sx={{
							margin: "16px",
							borderRadius: "8px"
						}}
					>
						<CardHeader
							title={
								<Typography variant="h6">
									{t("freelancerDetails.evaluations.title")}
								</Typography>
							}
						/>
						<Tabs
							value={evaluationTabIndex}
							onChange={(_, newValue) => setEvaluationTabIndex(newValue)}
							sx={{ px: 2 }}
						>
							<Tab label={t("freelancerDetails.evaluations.tabClient")} />
							<Tab label={t("freelancerDetails.evaluations.tabFreelancer")} />
						</Tabs>
						<Divider />
						<CardContent>
							{evaluationTabIndex === 0 && (
								<>
									{evaluationSummary?.evaluations && evaluationSummary.evaluations.length > 0 ? (
										<Stack spacing={2} divider={<Divider />}>
											{evaluationSummary.evaluations.map(evaluation => (
												<EvaluationDisplay
													key={evaluation.id}
													evaluation={evaluation}
													variant="simple"
													showProjectInfo
													showEvaluatorName={currentUser?.type === UserType.Internal}
												/>
											))}
										</Stack>
									) : (
										<Typography variant="body2" color="text.secondary">
											{t("freelancerDetails.evaluations.noEvaluations")}
										</Typography>
									)}
								</>
							)}
							{evaluationTabIndex === 1 && (
								<>
									{freelancerEvaluationSummary?.evaluations && freelancerEvaluationSummary.evaluations.length > 0 ? (
										<Stack spacing={2} divider={<Divider />}>
											{freelancerEvaluationSummary.evaluations.map(evaluation => (
												<EvaluationDisplay
													key={evaluation.id}
													evaluation={evaluation}
													variant="simple"
													showProjectInfo
													showEvaluatorName={false}
												/>
											))}
										</Stack>
									) : (
										<Typography variant="body2" color="text.secondary">
											{t("freelancerDetails.evaluations.noEvaluationsFreelancer")}
										</Typography>
									)}
								</>
							)}
						</CardContent>
					</Card>
				)}
				{/* Talent Relationship Tracking Section */}
				{currentUser?.roles?.includes(UserRole.TAM) && showNotes && (
					<ApplicationNotes freelancerProfile={freelancerProfile} />
				)}
			</Grid2>
		</Grid2>
	);
}
